import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { getAudioLength } from './audioMetadata';
import { transcribeAudio } from './api';
import { annotateTranscriptionError, getErrorDiagnostics } from './errors';
import { createAudioJobId, removeAudioJobFromIndexedDB, saveFileToIndexedDB } from './jobStorage';
import { validateMediaFile } from './mediaFormats';
import { runTranscriptionSession } from './session';
import { canStartTranscription, initialSessionState, sessionReducer, TRANSCRIPTION_STATUS } from './sessionState';

async function extractOperationalChunk(file, { startSeconds, durationSeconds }) {
  const { extractChunk } = await import('./chunking');
  let buffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (error) {
    throw annotateTranscriptionError(error, { phase: 'file-read' });
  }
  const chunk = await extractChunk(buffer, file, startSeconds, durationSeconds);
  return new Blob([chunk], { type: 'audio/mpeg' });
}

export function useTranscriptionSession() {
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [startSeconds, setStartSeconds] = useState(0);
  const [endSeconds, setEndSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const abortRef = useRef(null);
  const jobIdRef = useRef(null);
  const audioUrlRef = useRef(null);

  const releaseAudioUrl = useCallback(() => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = null;
    setAudioUrl(null);
  }, []);

  const cleanupStorage = useCallback(async () => {
    if (!jobIdRef.current) return;
    const jobId = jobIdRef.current;
    jobIdRef.current = null;
    await removeAudioJobFromIndexedDB(jobId);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(async () => {
    cancel();
    await cleanupStorage();
    releaseAudioUrl();
    setFile(null);
    setDuration(0);
    setStartSeconds(0);
    setEndSeconds(0);
    dispatch({ type: 'reset' });
  }, [cancel, cleanupStorage, releaseAudioUrl]);

  const selectFile = useCallback(async (nextFile) => {
    validateMediaFile(nextFile);
    cancel();
    await cleanupStorage();
    releaseAudioUrl();
    setFile(null);
    setDuration(0);
    setStartSeconds(0);
    setEndSeconds(0);
    dispatch({ type: 'preparing' });

    try {
      const jobId = createAudioJobId();
      jobIdRef.current = jobId;
      // Persistence is best-effort; transcription continues with the in-memory File.
      await saveFileToIndexedDB(nextFile, jobId);
      const nextDuration = await getAudioLength(nextFile);
      const nextAudioUrl = URL.createObjectURL(nextFile);
      audioUrlRef.current = nextAudioUrl;
      setAudioUrl(nextAudioUrl);
      setFile(nextFile);
      setDuration(nextDuration);
      setStartSeconds(0);
      setEndSeconds(nextDuration);
      dispatch({ type: 'file-ready' });
    } catch (error) {
      await cleanupStorage();
      dispatch({ type: 'reset' });
      throw annotateTranscriptionError(error, { phase: 'media-metadata' });
    }
  }, [cancel, cleanupStorage, releaseAudioUrl]);

  const start = useCallback(async ({ language, mode, token, onContribution }) => {
    if (!file || !canStartTranscription(state.status)) return;
    const controller = new AbortController();
    abortRef.current = controller;
    dispatch({ type: 'preparing' });
    const contributionPromise = onContribution?.(file)?.catch((error) => ({ error }));

    try {
      await runTranscriptionSession({
        file,
        startSeconds,
        endSeconds,
        signal: controller.signal,
        extractChunk: extractOperationalChunk,
        transcribe: (audio, signal) => transcribeAudio({ audio, language, mode, token, signal }),
        onChunk: (chunks) => dispatch({ type: 'chunks', chunks }),
        onProgress: (progress) => dispatch({ type: 'transcribing', ...progress }),
      });
      dispatch({ type: 'complete' });
    } catch (error) {
      const annotatedError = annotateTranscriptionError(error, { phase: 'transcription' });
      dispatch(annotatedError.name === 'AbortError'
        ? { type: 'cancelled' }
        : {
          type: 'error',
          error: annotatedError.message || 'La transcripció ha fallat.',
          errorDetails: getErrorDiagnostics(annotatedError),
        });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      await cleanupStorage();
      void contributionPromise;
    }
  }, [cleanupStorage, endSeconds, file, startSeconds, state.status]);

  useEffect(() => () => {
    abortRef.current?.abort();
    if (jobIdRef.current) void removeAudioJobFromIndexedDB(jobIdRef.current);
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  useEffect(() => {
    const active = state.status === TRANSCRIPTION_STATUS.PREPARING || state.status === TRANSCRIPTION_STATUS.TRANSCRIBING;
    if (!active) return undefined;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [state.status]);

  return {
    ...state,
    audioUrl,
    cancel,
    duration,
    endSeconds,
    file,
    reset,
    selectFile,
    setEndSeconds,
    setStartSeconds,
    start,
    startSeconds,
  };
}
